import { create } from 'zustand';

import { Module, ModuleActions, ModulePrice, ModuleState, TOC } from './types';

const initialModuleState: ModuleState = {
  modulesUpdater: 0,
  modulesByPlan: new Map(),
  recommendations: [],
  plans: [],
  trialPaymentNeeded: false,
  trialPrice: undefined,

  module: undefined,
  plan: undefined,

  toc: [],
  isReviewLeft: false,
  overallProgress: 0,

  activeVideoId: undefined,
};

export const useModuleStore = create<ModuleState & ModuleActions>()((set) => ({
  ...initialModuleState,

  updateModules: () => set((state) => ({ modulesUpdater: state.modulesUpdater + 1 })),
  setModulesByPlan: (modulesByPlan: Map<string, Module[]>) => set({ modulesByPlan }),
  updateModuleByPlan: (planId: string, module: Module) =>
    set((state) => ({
      modulesByPlan: state.modulesByPlan.set(
        planId,
        (state.modulesByPlan.get(planId) || []).map((m) => (m.id === module.id ? module : m)),
      ),
    })),
  setRecommendations: (recommendations: Module[]) => set({ recommendations }),
  setPlans: (plans: Module[]) => set({ plans }),
  setTrialPaymentNeeded: (trialPaymentNeeded: boolean) => set({ trialPaymentNeeded }),
  setTrialPrice: (trialPrice?: ModulePrice) => set({ trialPrice }),

  setModule: (module?: Module) => set({ module }),
  setPlan: (plan?: string) => set({ plan }),
  completeSections: (moduleId: string, lessonId: string, sectionsAmount: number) =>
    set((state) => {
      if (
        !state.module ||
        state.module.id !== moduleId ||
        state.module.stats.articlesCompleted >= state.module.stats.articlesTotal
      ) {
        return state;
      }

      const lessonIndex = state.module.syllabus.findIndex((s) => s.id === lessonId);

      if (
        lessonIndex < 0 ||
        state.module.syllabus[lessonIndex].stats.completedCount >=
          state.module.syllabus[lessonIndex].stats.totalCount
      ) {
        return state;
      }

      const newModule = { ...state.module };

      newModule.stats.articlesCompleted += sectionsAmount;
      newModule.syllabus[lessonIndex].stats.completedCount += sectionsAmount;

      if (
        newModule.syllabus[lessonIndex].stats.completedCount >=
        newModule.syllabus[lessonIndex].stats.totalCount
      ) {
        newModule.stats.lessonCompleted += 1;
        newModule.syllabus[lessonIndex].stats.completed = true;
      }

      newModule.stats.progress =
        ((newModule.stats.articlesCompleted / (newModule.stats.articlesTotal || 1) +
          newModule.stats.lessonCompleted / (newModule.stats.lessonTotal || 1) +
          newModule.stats.videoCompleted / (newModule.stats.videoTotal || 1) +
          newModule.stats.quizCompleted / (newModule.stats.quizTotal || 1)) /
          (+Boolean(newModule.stats.articlesTotal) +
            +Boolean(newModule.stats.lessonTotal) +
            +Boolean(newModule.stats.videoTotal) +
            +Boolean(newModule.stats.quizTotal))) *
        100;

      if (newModule.stats.progress >= 100) {
        newModule.stats.progress = 100;
        newModule.stats.completed = true;
      }

      return {
        module: newModule,
      };
    }),
  completeQuiz: (moduleId: string, quizId: string, score: number) =>
    set((state) => {
      if (
        !state.module ||
        state.module.id !== moduleId ||
        state.module.stats.quizCompleted >= state.module.stats.quizTotal
      ) {
        return state;
      }

      const quizIndex = state.module.quizzes.findIndex((q) => q.id === quizId);

      if (quizIndex < 0 || state.module.quizzes[quizIndex].completed) {
        return state;
      }

      const newModule = { ...state.module };

      newModule.stats.quizCompleted += 1;
      newModule.quizzes[quizIndex].completed = true;
      newModule.quizzes[quizIndex].score = score;

      newModule.stats.progress =
        ((newModule.stats.articlesCompleted / (newModule.stats.articlesTotal || 1) +
          newModule.stats.lessonCompleted / (newModule.stats.lessonTotal || 1) +
          newModule.stats.videoCompleted / (newModule.stats.videoTotal || 1) +
          newModule.stats.quizCompleted / (newModule.stats.quizTotal || 1)) /
          (+Boolean(newModule.stats.articlesTotal) +
            +Boolean(newModule.stats.lessonTotal) +
            +Boolean(newModule.stats.videoTotal) +
            +Boolean(newModule.stats.quizTotal))) *
        100;

      if (newModule.stats.progress >= 100) {
        newModule.stats.progress = 100;
        newModule.stats.completed = true;
      }

      return {
        module: newModule,
      };
    }),
  failQuiz: (moduleId: string, quizId: string) =>
    set((state) => {
      if (!state.module || state.module.id !== moduleId || state.module.stats.failed) {
        return state;
      }

      const quizIndex = state.module.quizzes.findIndex((q) => q.id === quizId);

      if (quizIndex < 0 || state.module.quizzes[quizIndex].failed) {
        return state;
      }

      const newModule = { ...state.module };

      newModule.quizzes[quizIndex].failed = true;
      newModule.stats.failed = true;

      return {
        module: newModule,
      };
    }),
  completeVideo: (moduleId: string, videoId: string) =>
    set((state) => {
      if (
        !state.module ||
        state.module.id !== moduleId ||
        state.module.stats.videoCompleted >= state.module.stats.videoTotal
      ) {
        return state;
      }

      const videoIndex = state.module.videos.findIndex((v) => v.id === videoId);

      if (videoIndex < 0 || state.module.videos[videoIndex].completed) {
        return state;
      }

      const newModule = { ...state.module };

      newModule.stats.videoCompleted += 1;
      newModule.videos[videoIndex].completed = true;

      newModule.stats.progress =
        ((newModule.stats.articlesCompleted / (newModule.stats.articlesTotal || 1) +
          newModule.stats.lessonCompleted / (newModule.stats.lessonTotal || 1) +
          newModule.stats.videoCompleted / (newModule.stats.videoTotal || 1) +
          newModule.stats.quizCompleted / (newModule.stats.quizTotal || 1)) /
          (+Boolean(newModule.stats.articlesTotal) +
            +Boolean(newModule.stats.lessonTotal) +
            +Boolean(newModule.stats.videoTotal) +
            +Boolean(newModule.stats.quizTotal))) *
        100;

      if (newModule.stats.progress >= 100) {
        newModule.stats.progress = 100;
        newModule.stats.completed = true;
      }

      return {
        module: newModule,
      };
    }),

  setTOC: (toc: TOC[]) => set({ toc }),
  setReviewLeft: (isReviewLeft: boolean) => set({ isReviewLeft }),
  setOverallProgress: (overallProgress: number) => set({ overallProgress }),

  setActiveVideoId: (activeVideoId?: string) => set({ activeVideoId }),
}));
